import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { GraduationCap, Clock, FileQuestion, Ban, Signal, Users, Award, CheckCircle2, Loader2, ArrowRight, ShieldCheck, Brain, MonitorSmartphone, MessageSquareText, Puzzle, Briefcase } from "lucide-react";
import { API, getAttribution } from "@/lib/contact";

const MEASURES = [
  { icon: Briefcase, title: "Business Thinking", desc: "How you approach real business situations" },
  { icon: MonitorSmartphone, title: "Digital Awareness", desc: "Comfort with everyday digital tools" },
  { icon: Brain, title: "Logical Reasoning", desc: "Patterns, numbers and structured thinking" },
  { icon: MessageSquareText, title: "Communication & Commercial Aptitude", desc: "Customer handling and money sense" },
  { icon: Puzzle, title: "Practical Problem Solving", desc: "Day-to-day business decision making" },
];

const FACTS = [
  { icon: FileQuestion, label: "25 Questions" },
  { icon: Award, label: "25 Marks" },
  { icon: Clock, label: "30 Minutes" },
  { icon: Ban, label: "No Negative Marking" },
  { icon: Signal, label: "Easy to Moderate" },
  { icon: Users, label: "Class 11, 12, College & Graduates" },
];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-slate-400 outline-none focus:border-electric focus:ring-2 focus:ring-electric/15 transition-all";

const F = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</span>
    {children}
  </label>
);

const normalizeMobile = (raw) => {
  let d = (raw || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d;
};

export default function ScholarshipPage() {
  const [config, setConfig] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [form, setForm] = useState({
    name: "", student_mobile: "", parent_mobile: "", age: "", qualification: "",
    college: "", city: "", laptop_access: "", interest: "", joining_timeline: "",
    parent_discussion: "", session_id: "", lead_source: "Organic",
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Scholarship Test 2026 | LaunchX Academy Rampur";
    axios.get(`${API}/scholarship/config`).then((r) => setConfig(r.data)).catch(() => {});
    axios.get(`${API}/scholarship/sessions`).then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) return setError("Please enter the student's full name.");
    if (!/^[6-9]\d{9}$/.test(normalizeMobile(form.student_mobile))) return setError("Please enter a valid 10-digit student mobile number.");
    if (!/^[6-9]\d{9}$/.test(normalizeMobile(form.parent_mobile))) return setError("Please enter a valid 10-digit parent mobile number.");
    if (!form.age || +form.age < 10 || +form.age > 60) return setError("Please enter a valid age.");
    if (!form.qualification || !form.laptop_access || !form.interest || !form.joining_timeline || !form.parent_discussion) return setError("Please fill all required fields.");
    if (!form.session_id) return setError("Please choose a test session.");
    if (!consent) return setError("Please accept the privacy notice to register.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/scholarship/register`, {
        ...form, age: +form.age, consent, website: "", ...getAttribution(),
      });
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-4" data-testid="scholarship-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg w-full bg-navy-800 border border-white/10 rounded-3xl p-8 sm:p-10 text-center">
          <span className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
            <CheckCircle2 size={30} className="text-emerald-400" />
          </span>
          <h1 className="mt-6 font-display font-bold text-white text-2xl">Registration Successful</h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            You are registered for the LaunchX Business &amp; Digital Skills Scholarship Test. Our team will contact you with test-day instructions.
          </p>
          <div className="mt-7 bg-navy rounded-2xl p-5 text-left space-y-2.5">
            <p className="text-sm text-slate-300">Student ID: <span className="font-display font-bold text-cyanx" data-testid="success-student-id">{done.student_id}</span></p>
            <p className="text-sm text-slate-300">Registration ID: <span className="font-display font-bold text-white">{done.registration_id}</span></p>
            <p className="text-sm text-slate-300">Session: <span className="font-display font-semibold text-white">{done.session}</span></p>
            {done.registration_status === "waitlist" && <p className="text-xs text-amber-400">You are on the waitlist for this session.</p>}
          </div>
          <p className="mt-4 text-xs text-slate-500">Please save your Student ID — you will need it on test day. Keep your registered mobile number available.</p>
          <Link to="/" data-testid="scholarship-home-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-cyanx hover:text-white transition-colors">
            Return to LaunchX Home <ArrowRight size={15} />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-body" data-testid="scholarship-page">
      <header className="bg-navy border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="scholarship-logo"><img src="/logo-full.png" alt="LaunchX Academy" className="h-8 w-auto" /></Link>
          <a href="#register" data-testid="scholarship-nav-cta" className="text-[13px] font-semibold text-white bg-electric hover:bg-electric-dark rounded-full px-5 py-2.5 transition-colors">
            Register Now
          </a>
        </div>
      </header>

      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 bg-grid-navy" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-electric/20 blur-[140px]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 border border-cyanx/30 bg-cyanx/5 rounded-full px-4 py-1.5">
            <GraduationCap size={14} className="text-cyanx" />
            <span className="text-[11px] font-bold tracking-[0.2em] text-cyanx uppercase">Merit-Based Scholarship Opportunity</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-6 font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12]" data-testid="scholarship-hero-title">
            LaunchX Business &amp; Digital Skills
            <span className="block text-gradient mt-2">Scholarship Test 2026</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="mt-4 text-base sm:text-lg text-slate-400">
            Discover Your Digital Business Potential
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mt-9 flex flex-wrap justify-center gap-2.5" data-testid="scholarship-facts">
            {FACTS.map((f) => (
              <span key={f.label} className="inline-flex items-center gap-2 text-xs font-semibold text-slate-200 border border-white/15 rounded-full px-4 py-2">
                <f.icon size={13} className="text-cyanx" /> {f.label}
              </span>
            ))}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mt-9 flex flex-wrap justify-center gap-4">
            <a href="#register" data-testid="scholarship-cta-register" className="inline-flex items-center gap-2 bg-electric hover:bg-electric-dark text-white font-semibold text-sm rounded-full px-8 py-4 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-electric/40">
              Register for Scholarship Test <ArrowRight size={16} />
            </a>
            <a href="#details" data-testid="scholarship-cta-details" className="inline-flex items-center gap-2 border border-white/20 hover:border-cyanx/60 text-slate-200 font-semibold text-sm rounded-full px-8 py-4 transition-colors">
              View Test Details
            </a>
          </motion.div>
        </div>
      </section>

      <section id="details" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-electric">What the Test Measures</p>
            <h2 className="mt-3 font-display font-bold text-navy text-2xl sm:text-3xl">A Professional Aptitude Assessment — Not a Memory Exam</h2>
            <p className="mt-3 text-sm sm:text-base text-slate-500">No advanced knowledge of advertising, coding, SEO or finance is needed. Questions are designed for Class 11–12 level and above.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MEASURES.map((m, i) => (
              <motion.div key={m.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="bg-background border border-slate-200/80 rounded-2xl p-6 hover:border-electric/40 hover:shadow-lg transition-all" data-testid={`measure-card-${i + 1}`}>
                <span className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><m.icon size={18} className="text-cyanx" /></span>
                <h3 className="mt-4 font-display font-semibold text-navy text-base">{m.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500">{m.desc}</p>
              </motion.div>
            ))}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
              className="bg-navy rounded-2xl p-6 flex flex-col justify-center">
              <ShieldCheck size={22} className="text-cyanx" />
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                Scholarship eligibility depends on verified test performance and LaunchX Academy scholarship rules. Participation does not automatically guarantee a scholarship.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="register" className="py-20 bg-background bg-grid-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-electric">Registration</p>
            <h2 className="mt-3 font-display font-bold text-navy text-2xl sm:text-3xl">Register for the Scholarship Test</h2>
            <p className="mt-3 text-sm text-slate-500">Takes about 2 minutes. You will receive a unique Student ID after registration.</p>
          </div>
          <form onSubmit={submit} className="mt-10 bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-9 shadow-sm space-y-5" data-testid="scholarship-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Student Full Name *" testid="reg-name-field"><input data-testid="reg-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Full name" /></F>
              <F label="Age *" testid="reg-age-field"><input data-testid="reg-age-input" required type="number" min="10" max="60" value={form.age} onChange={set("age")} className={inputCls} placeholder="Age" /></F>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Student Mobile Number *" testid="reg-mobile-field"><input data-testid="reg-mobile-input" required type="tel" value={form.student_mobile} onChange={set("student_mobile")} className={inputCls} placeholder="10-digit mobile" /></F>
              <F label="Parent/Guardian Mobile *" testid="reg-parent-field"><input data-testid="reg-parent-input" required type="tel" value={form.parent_mobile} onChange={set("parent_mobile")} className={inputCls} placeholder="10-digit mobile" /></F>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Current Qualification *" testid="reg-qualification-field">
                <select data-testid="reg-qualification-select" required value={form.qualification} onChange={set("qualification")} className={inputCls}>
                  <option value="" disabled>Select qualification</option>
                  {(config?.qualifications || []).map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </F>
              <F label="School / College Name" testid="reg-college-field"><input data-testid="reg-college-input" value={form.college} onChange={set("college")} className={inputCls} placeholder="School or college" /></F>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="City / Area *" testid="reg-city-field"><input data-testid="reg-city-input" required value={form.city} onChange={set("city")} className={inputCls} placeholder="e.g. Rampur" /></F>
              <F label="Laptop / Computer Access *" testid="reg-laptop-field">
                <select data-testid="reg-laptop-select" required value={form.laptop_access} onChange={set("laptop_access")} className={inputCls}>
                  <option value="" disabled>Select access</option>
                  {(config?.laptop_access || []).map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </F>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Primary Interest *" testid="reg-interest-field">
                <select data-testid="reg-interest-select" required value={form.interest} onChange={set("interest")} className={inputCls}>
                  <option value="" disabled>Select interest</option>
                  {(config?.interests || []).map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </F>
              <F label="Expected Joining Timeline *" testid="reg-timeline-field">
                <select data-testid="reg-timeline-select" required value={form.joining_timeline} onChange={set("joining_timeline")} className={inputCls}>
                  <option value="" disabled>Select timeline</option>
                  {(config?.joining_timelines || []).map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </F>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Discussed With Parents? *" testid="reg-parent-discussion-field">
                <select data-testid="reg-parent-discussion-select" required value={form.parent_discussion} onChange={set("parent_discussion")} className={inputCls}>
                  <option value="" disabled>Select</option>
                  {(config?.parent_discussion || []).map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </F>
              <F label="How Did You Hear About Us?" testid="reg-source-field">
                <select data-testid="reg-source-select" value={form.lead_source} onChange={set("lead_source")} className={inputCls}>
                  {(config?.lead_sources || []).map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </F>
            </div>
            <F label="Preferred Test Session *" testid="reg-session-field">
              <div className="space-y-2.5" data-testid="reg-session-options">
                {sessions.length === 0 && <p className="text-sm text-slate-400 border border-slate-200 rounded-xl px-4 py-3.5">No sessions are currently open. Please check back soon.</p>}
                {sessions.map((s) => (
                  <label key={s.id} data-testid={`reg-session-${s.session_code}`}
                    className={`flex items-center justify-between gap-3 border rounded-xl px-4 py-3.5 cursor-pointer transition-all ${
                      form.session_id === s.id ? "border-electric bg-electric/5 ring-2 ring-electric/15" : "border-slate-200 hover:border-electric/40"
                    } ${s.availability === "Full" ? "opacity-50 pointer-events-none" : ""}`}>
                    <span className="flex items-center gap-3">
                      <input type="radio" name="session" checked={form.session_id === s.id} onChange={() => setForm({ ...form, session_id: s.id })} className="accent-electric w-4 h-4" />
                      <span>
                        <span className="block text-sm font-semibold text-navy">{s.name}</span>
                        <span className="block text-xs text-slate-500">{s.date} · {s.start_time} · {s.venue}</span>
                      </span>
                    </span>
                    <span className={`text-[11px] font-bold rounded-full px-3 py-1 shrink-0 ${
                      s.availability === "Available" ? "bg-emerald-50 text-emerald-600" : s.availability === "Almost Full" ? "bg-amber-50 text-amber-600" : s.availability === "Waitlist" ? "bg-blue-50 text-electric" : "bg-red-50 text-red-500"
                    }`}>{s.availability === "Available" ? `${s.seats_left} seats left` : s.availability}</span>
                  </label>
                ))}
              </div>
            </F>

            <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" onChange={() => {}} />

            <label className="flex items-start gap-3 cursor-pointer" data-testid="reg-consent-field">
              <input data-testid="reg-consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-electric cursor-pointer" />
              <span className="text-xs text-slate-500 leading-relaxed">
                {config?.privacy_notice} I agree to the{" "}
                <Link to="/privacy-policy" target="_blank" className="text-electric font-semibold hover:underline">Privacy Policy</Link> and{" "}
                <Link to="/terms-and-conditions" target="_blank" className="text-electric font-semibold hover:underline">Terms &amp; Conditions</Link>.
              </span>
            </label>

            {error && <p data-testid="reg-error" className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">{error}</p>}

            <button data-testid="reg-submit-button" type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-electric hover:bg-electric-dark disabled:opacity-60 text-white font-semibold text-sm rounded-xl px-7 py-4 transition-all hover:shadow-lg hover:shadow-electric/30">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <GraduationCap size={16} />}
              {loading ? "Registering..." : "Register for Scholarship Test"}
            </button>
          </form>
        </div>
      </section>

      <footer className="bg-[#070C16] border-t border-white/5 py-8 text-center">
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} LaunchX Academy, Rampur, Uttar Pradesh · <Link to="/" className="text-slate-400 hover:text-cyanx">launchxacademy.com</Link></p>
      </footer>
    </div>
  );
}
